import mixpanel, {
  type Callback,
  type Config,
  type Dict,
  type RequestOptions,
} from 'mixpanel-browser';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { ANALYTICS_EVENTS, type TrackableEventName } from '@/stores/analytics-events';

const ready = ref(false);

function initMixpanel() {
  const mixpanelConfig: Partial<Config> = { track_pageview: false, secure_cookie: true };

  if (env.mixpanelApiHost) {
    mixpanelConfig.api_host = env.mixpanelApiHost;
  }

  const enabled = env.mixpanelEnabled && !isDatadogSyntheticsBrowser && !window.Cypress;
  if (enabled) {
    mixpanel.init(env.mixpanelToken, mixpanelConfig);
    ready.value = true;
  } else {
    mixpanel.init(null as unknown as string, { ...mixpanelConfig, test: true }, 'mixpanel');
    mixpanel.disable();
  }
}
initMixpanel();

export const useMixpanelStore = defineStore('mixpanel', () => {
  function track(
    eventName: TrackableEventName,
    ...args: [properties?: Dict, optionsOrCallback?: RequestOptions | Callback, callback?: Callback]
  ) {
    mixpanel.track(eventName, ...args);
  }

  /**
   * Track an event immediately, bypassing the batch queue.
   * Returns a promise that resolves once the request completes.
   * Use this when a navigation (e.g. window.location.assign) follows
   * the track call and you need delivery confirmed first.
   */
  function trackImmediately(eventName: TrackableEventName, properties?: Dict): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      // Safety net: never block the caller longer than 2s if the
      // Mixpanel callback doesn't fire (ad blocker, offline,
      // mixpanel.disable() no-op path, etc.).
      setTimeout(done, 2000);
      mixpanel.track(eventName, properties, { send_immediately: true }, done);
    });
  }

  /**
   * Track an event and wait for Mixpanel to acknowledge delivery.
   * Resolves on a successful response, or if the callback never fires
   * within the timeout (mixpanel.disable() in dev, ad blocker, offline) —
   * none of which are user-actionable. Rejects only when Mixpanel reports
   * an explicit delivery failure (e.g. /track returns an HTTP error).
   *
   * Use when the user is invested in whether the event was logged
   * (e.g. AI summary feedback). For fire-and-forget analytics, use track().
   */
  function trackAndAck(eventName: TrackableEventName, properties?: Dict): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = <T>(fn: (value: T) => void, value: T) => {
        if (settled) return;
        settled = true;
        fn(value);
      };
      setTimeout(() => settle(resolve, undefined), 5000);
      mixpanel.track(eventName, properties, { send_immediately: true }, (response) => {
        // mixpanel-browser callback: response is 1 on success, 0 on failure.
        if (response === 0) {
          settle(reject, new Error('Mixpanel track delivery failed'));
        } else {
          settle(resolve, undefined);
        }
      });
    });
  }

  function registerSuperProperties(properties?: Dict) {
    if (properties) {
      mixpanel.register(properties);
    }
  }

  function trackPage({
    page,
    payload,
    unload,
  }: {
    page: string | null;
    payload?: Dict;
    unload?: boolean;
  }) {
    if (unload) {
      mixpanel.set_config({ api_transport: 'sendBeacon' });
    }
    registerSuperProperties({ page_sp_test: page });
    track(ANALYTICS_EVENTS.PAGE.PAGE_VIEWED, payload);
    mixpanel.time_event(ANALYTICS_EVENTS.PAGE.PAGE_VIEWED);
  }

  function setIdentity(properties: Dict & { userId?: string | number } = {}) {
    if (properties) {
      // cast not String() — preserve numeric distinct_id, avoids fragmenting profiles (PR #25051)
      mixpanel.identify(
        properties.userId != null ? (properties.userId as unknown as string) : undefined,
      );
      mixpanel.people.set(properties);
    }
  }

  function setUtmQueryParams({
    firstTouchParams = {},
    lastTouchParams = {},
  }: { firstTouchParams?: Dict; lastTouchParams?: Dict } = {}) {
    if (Object.keys(firstTouchParams).length > 0) {
      mixpanel.people.set_once(firstTouchParams);
    }
    mixpanel.people.set({
      ...lastTouchParams,
    });
    registerSuperProperties({
      ...lastTouchParams,
    });
  }

  function reset() {
    mixpanel.reset();
  }

  return {
    ready,
    track,
    trackImmediately,
    trackAndAck,
    trackPage,
    registerSuperProperties,
    reset,
    setUtmQueryParams,
    setIdentity,
  };
});
